import { useReducer, useRef, useMemo, useCallback, useContext, createContext, useEffect } from 'react';
import axios from 'axios';
import { getApiClient, isErrorCancel, makeCancelTokenSource, handleApiError } from '../api_client/get';
import { useUser } from './user';

const FALLBACK_AMOUNT = 0;
const FALLBACK_CURRENCY = 'USD';
const WALLET_CACHE_TTL_MS = 30_000;

const WalletContext = createContext(null);

const reducer = (state, action) => {
  switch (action.type) {
    case 'load-start':
      return { ...state, loading: true, errorMessage: null };
    case 'load-success':
      return {
        ...state,
        loading: false,
        balance: action.balance,
        credits: action.credits,
        currency: action.currency,
        cashPayoutSummary: action.summary
      };
    case 'load-failure':
      return { ...state, loading: false, errorMessage: action.message };
    case 'open':
      return { ...state, isOpen: true };
    case 'close':
      return { ...state, isOpen: false };
    case 'reset':
      return { ...initialState };
    default:
      return state;
  }
};

const initialState = {
  balance: FALLBACK_AMOUNT,
  credits: [],
  currency: FALLBACK_CURRENCY,
  cashPayoutSummary: [],
  loading: false,
  errorMessage: null,
  isOpen: false
};

export const WalletProvider = ({ children }) => {
  const { loggedIn } = useUser();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { balance, credits, currency, cashPayoutSummary, loading, errorMessage, isOpen } = state;
  const fetchCtsRef = useRef(null);
  const lastFetchedAtRef = useRef(null);
  const fetchInFlightRef = useRef(false);

  const fetchWallet = useCallback(async (cts) => {
    fetchInFlightRef.current = true;
    dispatch({ type: 'load-start' });
    try {
      const response = await getApiClient().getWallet(cts);
      if (response.data.success) {
        const totalBalance = response.data.total_balance;
        lastFetchedAtRef.current = Date.now();
        dispatch({
          type: 'load-success',
          balance: totalBalance.amount,
          currency: totalBalance.currency,
          credits: response.data.credits ?? [],
          summary: response.data.summary ?? []
        });
      } else {
        dispatch({ type: 'load-failure', message: 'Failed to fetch wallet data.' });
      }
    } catch (error) {
      if (isErrorCancel(error)) return;
      if (axios.isAxiosError(error)) {
        handleApiError(error, msg => dispatch({ type: 'load-failure', message: msg }));
      } else {
        dispatch({ type: 'load-failure', message: 'Something went wrong.' });
      }
    } finally {
      fetchInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!loggedIn) {
      if (fetchCtsRef.current) fetchCtsRef.current.cancel();
      dispatch({ type: 'reset' });
      lastFetchedAtRef.current = null;
      return;
    }
    const cts = makeCancelTokenSource();
    fetchCtsRef.current = cts;
    fetchWallet(cts);
    return () => cts.cancel();
  }, [loggedIn, fetchWallet]);

  const openWallet = useCallback(() => {
    dispatch({ type: 'open' });
    const isFresh = lastFetchedAtRef.current &&
      (Date.now() - lastFetchedAtRef.current < WALLET_CACHE_TTL_MS);
    if (isFresh || fetchInFlightRef.current) return;
    const cts = makeCancelTokenSource();
    fetchCtsRef.current = cts;
    fetchWallet(cts);
  }, [fetchWallet]);

  const closeWallet = useCallback(() => {
    dispatch({ type: 'close' });
  }, []);

  const value = useMemo(() => ({
    balance,
    credits,
    currency,
    cashPayoutSummary,
    loading,
    errorMessage,
    isOpen,
    openWallet,
    closeWallet
  }), [
    balance, credits, currency, cashPayoutSummary,
    loading, errorMessage, isOpen,
    openWallet, closeWallet
  ]);

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) throw new Error('useWallet must be used within a WalletProvider');
  return context;
};

export default WalletContext;
